import { clsx } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";
import tippy from "tippy.js";
import dayjs from "dayjs";
import quarterExtension from "dayjs/plugin/quarterOfYear";
import pluralizeLib from "pluralize";
dayjs.extend(quarterExtension);

// Configure Tailwind-merge so it doesn't remove custom classes with a named prefix
// similar to built-in tailwind classes.
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      // Include text-input-* in both color and font-size because `text-*` is the built in tailwind
      // prefix for both.
      "font-size": ["text-tiny", "text-input", "text-input-tiny"],
      color: ["text-input", "text-input-tiny"],
    },
  },
});

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const pluralRules = new Intl.PluralRules("en-US");

/**
 * Pluralize a word based on the count.
 *
 * @param {string} text
 * @param {number} count
 * @returns {string}
 */
export function pluralize(text, count = null) {
  if (count != null && pluralRules.select(count) === "one") {
    return pluralizeLib.singular(text);
  }
  return pluralizeLib.plural(text);
}

/**
 * Pluralize a word based on the count. Includes both the count and the word in the return value.
 *
 * @param {string} text
 * @param {number} count
 * @returns {string}
 */
export function pluralizeWithCount(text, count) {
  if (pluralRules.select(count) === "one") {
    return `${count} ${pluralizeLib.singular(text)}`;
  }
  const numeral = count === null ? "" : `${count} `;
  return `${numeral}${pluralizeLib.plural(text)}`;
}

// Function to convert numbers to words (1 to "one", 2 to "two", etc.)
export function numberToWords(num) {
  const words = [
    "zero",
    "one",
    "two",
    "three",
    "four",
    "five",
    "six",
    "seven",
    "eight",
    "nine",
    "ten",
    "eleven",
    "twelve",
    "thirteen",
    "fourteen",
    "fifteen",
    "sixteen",
    "seventeen",
    "eighteen",
    "nineteen",
    "twenty",
  ];

  if (num >= 0 && num <= 20) {
    return words[num];
  }

  // For larger numbers, you might want to use a more comprehensive solution
  // or a library like number-to-words
  return num.toString();
}

export function getQueryParam(key) {
  const queryParams = new URLSearchParams(window.location.search);
  return queryParams.get(key);
}

export function getAllQueryParams(key) {
  const queryParams = new URLSearchParams(window.location.search);
  return queryParams.getAll(key);
}

export function setQueryParam(key, value) {
  // Update url query param without reloading the page
  const url = new URL(window.location);
  url.searchParams.set(key, value);
  window.history.pushState({}, "", url);
}

export function appendQueryParam(key, value) {
  // Update url query param without reloading the page
  const url = new URL(window.location);
  url.searchParams.append(key, value);
  window.history.pushState({}, "", url);
}

export function deleteQueryParam(key) {
  // Update url query param without reloading the page
  const url = new URL(window.location);
  url.searchParams.delete(key);
  window.history.pushState({}, "", url);
}

export function pick(obj, arr) {
  return Object.fromEntries(
    Object.entries(obj).filter(([k]) => arr.includes(k)),
  );
}

export function except(obj, arr) {
  return Object.fromEntries(
    Object.entries(obj).filter(([k]) => !arr.includes(k)),
  );
}

export function debounce(func, timeout = 300) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args);
    }, timeout);
  };
}

export function indexBy(array, attr) {
  return array.reduce((accumulator, currentValue) => {
    const key = currentValue[attr];
    accumulator[key] = currentValue;
    return accumulator;
  }, {});
}

export function tooltipIfTruncated(e, fullText) {
  const el = e.target;
  el._tippy?.destroy(); // Delete previous instance

  if (el && el.offsetWidth < el.scrollWidth) {
    tippy(el, {
      content: fullText,
      delay: [250, 0],
      onShow: (instance) => {
        tippy.hideAll({ exclude: instance });
      },
    });
  }
}

export function truncateText(text, maxLength) {
  if (!text) return text;

  return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
}

export function downloadFile(url, name = null) {
  // Create an anchor element
  const link = document.createElement("a");

  // Set the URL for the download
  link.href = url;

  // Set the name for the download
  if (name) {
    link.setAttribute("download", name);
  }

  // Append the anchor to the body (required for Firefox)
  document.body.appendChild(link);

  // Programmatically click the anchor
  link.click();

  // Remove the anchor from the document
  document.body.removeChild(link);
}

export function numberWithCommas(num, decimals) {
  // If the input is not a number, attempt to parse it.
  const numericValue =
    typeof num === "number"
      ? num
      : parseFloat(num?.toString()?.replace(/,/g, "")); // Remove commas from the string if present

  // If the parsing fails (NaN), return the original input.
  if (isNaN(numericValue)) {
    return num;
  }

  // Apply formatting based on the provided decimals.
  const numberString =
    typeof decimals === "number"
      ? numericValue.toFixed(decimals)
      : numericValue.toString();

  // Split into integer and decimal parts
  const parts = numberString.split(".");

  // Add commas only to the integer part
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  // Rejoin with decimal part if it exists
  return parts.join(".");
}

export function toInitials(name) {
  const parsed_name = name?.split(" ");

  if (parsed_name?.length > 1) {
    return (
      parsed_name[0][0] + parsed_name[parsed_name.length - 1][0]
    ).toUpperCase();
  }

  return parsed_name?.[0]?.slice(0, 2)?.toUpperCase();
}

export function containsUrl(text) {
  const urlPattern = /(https?:\/\/(www\.)?[^\s<]+|www\.[^\s<]+)/i;
  return Boolean(text?.trim()) && urlPattern.test(text.trim());
}

export function formatDateForInterval(d, interval, chartDateRange = null) {
  const day = dayjs(d);

  const { from: rangeFrom, to: rangeTo } = chartRangeToDayjs(chartDateRange);
  if (interval == "w") {
    let startAt = day;
    let endAt = day.add(6, "days");
    // cap interval dates by chart range if provided
    if (rangeFrom?.isAfter(startAt, "day")) {
      startAt = rangeFrom;
    }
    if (rangeTo?.isBefore(endAt, "day")) {
      endAt = rangeTo;
    }
    return `${startAt.format("MMM D")} - ${endAt.format("MMM D")}`;
  }

  const rangeSuffix = partialIntervalSuffix(d, interval, chartDateRange);

  if (interval == "m") {
    return `${day.format("MMM YYYY")}${rangeSuffix}`;
  }

  if (interval == "q") {
    const startFYMonth = window.currentAccount?.fiscal_year_start_month;
    if (startFYMonth && startFYMonth !== 1) {
      const monthRelativeToStart = day.month() - startFYMonth + 1; // FYI dayJS' months are 0-indexed - Ruby's are 1-indexed
      const quarter = Math.floor(((monthRelativeToStart + 12) % 12) / 3) + 1;

      let year;
      if (window.currentAccount.fiscal_year_label_type === "use_start") {
        year = monthRelativeToStart < 0 ? day.year() - 1 : day.year();
      } else {
        year = monthRelativeToStart < 0 ? day.year() : day.year() + 1;
      }

      return `Q${quarter} FY${year}${rangeSuffix}`;
    } else {
      // use the normal quarter logic
      return `Q${day.quarter()} ${day.year()}${rangeSuffix}`;
    }
  }

  return day.format("MMM D");
}

function chartRangeToDayjs(chartDateRange) {
  if (chartDateRange) {
    return { from: dayjs(chartDateRange.from), to: dayjs(chartDateRange.to) };
  } else {
    return {};
  }
}

export function intervalDateIsPartial(d, interval, chartDateRange) {
  const edges = intervalDatePartialEdges(d, interval, chartDateRange);
  return edges.length > 0;
}

// figure out whether the interval range for this date is only partially covered by the chart range
// i.e. the start is earlier than the chart start, and/or the interval end is after the chart end
function intervalDatePartialEdges(d, interval, chartDateRange) {
  const intervalStart = dayjs(d);
  if (!interval || !chartDateRange || interval === "d") return [];

  let edges = [];
  const { from, to } = chartRangeToDayjs(chartDateRange);

  if (intervalStart.isBefore(from, "day")) edges.push("start");

  let intervalEnd;
  if (interval === "w") {
    intervalEnd = intervalStart.add(6, "days");
  } else if (interval === "m") {
    intervalEnd = intervalStart.add(1, "months").subtract(1, "days");
  } else if (interval === "q") {
    intervalEnd = intervalStart.add(1, "quarters").subtract(1, "days");
  }

  if (intervalEnd?.isAfter(to, "day")) {
    edges.push("end");
  }
  return edges;
}

function partialIntervalSuffix(d, interval, chartDateRange) {
  const edges = intervalDatePartialEdges(d, interval, chartDateRange);
  const { from, to } = chartRangeToDayjs(chartDateRange);

  let parts = [];
  if (edges.includes("start")) {
    parts.push(`from ${from.format("MMM D")}`);
  }
  if (edges.includes("end")) {
    parts.push(`to ${to.format("MMM D")}`);
  }

  if (parts.length > 0) {
    return ` (${parts.join(" ")})`;
  } else {
    return "";
  }
}

export function calculateMedian(values) {
  const sorted = values.sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[middle - 1] + sorted[middle]) / 2
    : sorted[middle];
}

export function calculateTrendline(values) {
  // Calculate trend line using linear regression

  // Step 1: Convert categorical x-axis to numeric indices
  const points = values.map((value, index) => ({
    x: index,
    y: value,
  }));

  // Step 2: Calculate means of x and y
  const n = points.length;
  const meanX = points.reduce((sum, point) => sum + point.x, 0) / n;
  const meanY = points.reduce((sum, point) => sum + point.y, 0) / n;

  // Step 3: Calculate slope (m) using least squares method
  let numerator = 0;
  let denominator = 0;

  for (const point of points) {
    const xDiff = point.x - meanX;
    numerator += xDiff * (point.y - meanY);
    denominator += xDiff * xDiff;
  }

  const slope = denominator !== 0 ? numerator / denominator : 0;

  // Step 4: Calculate y-intercept (b) using y = mx + b
  const yIntercept = meanY - slope * meanX;

  // Step 5: Calculate start and end points for the trend line
  const startY = yIntercept;
  const endY = slope * (n - 1) + yIntercept;

  return {
    slope,
    yIntercept,
    start: startY,
    end: endY,
  };
}

export function abbreviateNumber(value) {
  if (value === null) return "";

  // Helper function to remove trailing .0
  const formatDecimal = (num) => {
    const fixed = num.toFixed(1);
    return fixed.endsWith(".0") ? fixed.slice(0, -2) : fixed;
  };

  // Format large numbers with M/B/T suffixes
  if (value >= 1000000000000) {
    return `${(value / 1000000000000).toFixed(1)}T`;
  }
  if (value >= 1000000000) {
    return `${(value / 1000000000).toFixed(1)}B`;
  }
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `${formatDecimal(value / 1000)}K`;
  }
  return value;
}

export function formatTimeUnitForBand(band) {
  if (!band) return null;

  if (band.includes("m")) {
    return "minute";
  }

  if (band.includes("h")) {
    return "hour";
  }

  if (band.includes("d")) {
    return "day";
  }

  if (band.includes("w")) {
    return "week";
  }
}

export function yTickFormatter(
  value,
  { pctInTarget = false, formatTimeResult = null } = {},
) {
  if (pctInTarget) {
    return `${value}%`;
  }

  if (formatTimeResult) {
    return `${abbreviateNumber(parseFloat(value))} ${formatTimeResult.abbrev}`;
  }

  return abbreviateNumber(value);
}

// If the value contains commas or quotes, wrap it in quotes and escape existing quotes
export function escapeCsvValue(value) {
  if (value == null || value == undefined) return "";

  const strValue = String(value);

  return strValue.includes(",") || strValue.includes('"')
    ? `"${strValue.replace(/"/g, '""')}"`
    : strValue;
}

// Sum the values of an object or array
// If key is provided, sum the values of the key in the object or array
// If key is not provided, sum the elements of the array
export function sum(obj, key) {
  if (Array.isArray(obj) && key) {
    return obj.reduce((acc, curr) => acc + curr[key], 0);
  }

  if (Array.isArray(obj) && !key) {
    return obj.reduce((acc, curr) => acc + curr, 0);
  }

  return Object.values(obj).reduce((acc, curr) => acc + curr[key], 0);
}

export const getDaysBetween = (startDate, endDate) => {
  const days = [];
  let endDay = dayjs(endDate);

  // Add 1 to include the start date
  const numDays = endDate.diff(startDate, "day") + 1;

  [...Array(numDays)].forEach(() => {
    days.push(endDay);
    endDay = endDay.subtract(1, "day");
  });

  return days.reverse();
};

export const isNumber = (value) => {
  return (
    value?.toString()?.length > 0 &&
    !isNaN(parseFloat(value)) &&
    isFinite(value)
  );
};

export const arrayEquals = (a, b) => {
  const arr1 = Array.from(a ?? []);
  const arr2 = Array.from(b ?? []);

  return (
    arr1.length === arr2.length &&
    JSON.stringify(arr1.sort()) == JSON.stringify(arr2.sort())
  );
};

export const toSnakeCase = (str) => {
  return (
    str
      // First, handle camelCase/PascalCase transitions
      .replace(/([a-z])([A-Z])/g, "$1_$2")
      // Then convert to lowercase
      .toLowerCase()
      // Replace sequences of non-alphanumeric characters with single underscore
      .replace(/[^a-z0-9]+/g, "_")
      // Remove leading and trailing underscores
      .replace(/^_+|_+$/g, "")
  );
};

export const capitalize = (str) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export function toSentence(arr) {
  if (arr.length < 2) {
    return arr.join(", ");
  }
  if (arr.length == 2) {
    return `${arr[0]} and ${arr[1]}`;
  }

  let sentence = "";
  sentence = arr.slice(0, arr.length - 1).join(", ");

  return sentence + `, and ${arr[arr.length - 1]}`;
}

export const getIsMac = () =>
  navigator.userAgent.toUpperCase().indexOf("MAC") >= 0;

export const scrubHtml = (html) => {
  return html
    ?.replace(/</g, "&lt;")
    ?.replace(/>/g, "&gt;")
    ?.replace(/`/g, "&#96;")
    ?.replace(/`/g, "&#96;")
    ?.replace(/'/g, "&#39;")
    ?.replace(/"/g, "&quot;");
};

/**
 * Convert the final set of parentheses to a hyphen.
 * e.g., "All companies (Java engineers) (25% APAC)" → "All companies (Java engineers) - 25% APAC"
 *
 * @param {string} text - The text to convert
 * @returns {string} - The converted text
 */
export const convertFinalParenthesesToHyphen = (text) => {
  if (!text) return text;
  return text.replace(/\s*\(([^)]+)\)$/, " - $1");
};

// Keep this in sync with the Ruby version in StringHelper.rb
export const applyPrLabel = (text, setting = null) => {
  if (!text) return text;

  if (typeof text !== "string") return text;

  const prLabelSetting = setting || window.currentAccount?.pr_label_setting;

  if (prLabelSetting === "merge_request") {
    let newText = text;
    newText = newText.replace(/Pull request/g, "Merge request");
    newText = newText.replace(/pull request/g, "merge request");
    newText = newText.replace(/PR/g, "MR");
    return newText;
  }

  return text;
};

export const preciseAdd = (a, b, decimals = 10) => {
  const multiplier = Math.pow(10, decimals);
  return Math.round((a + b) * multiplier) / multiplier;
};

export const safeParseJSON = (jsonString, defaultValue = []) => {
  if (!jsonString) return defaultValue;
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    console.error("Failed to parse JSON:", error);
    return defaultValue;
  }
};

export function getRandomElement(arr) {
  if (!arr || arr.length === 0) {
    return undefined;
  }
  const randomIndex = Math.floor(Math.random() * arr.length);
  return arr[randomIndex];
}
